import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { GameSessionKills } from './GameSessionKills'

export const GameSession = ({
    session,
    rowNumber,
    showUsername = false,
    openAbove = [],
}: {
    session: GameSession
    rowNumber: number
    showUsername?: boolean
    /** Sessions whose kills panels this row is already nested inside. */
    openAbove?: string[]
}) => {
    const [showDetails, setShowDetails] = useState(false)

    // A swept row is not a match anybody played to the end. Nothing reported a
    // result for it, so its close time is an estimate, its duration is that
    // estimate minus the spawn, and its kill count is inferred from the
    // victims' rows rather than stated by the server.
    //
    // Presenting those next to real sessions is what made them read as
    // suspicious: a four hour "match" with a kill count to match looks like a
    // player doing something impossible, when it is actually a row nobody
    // closed. So the estimates are withheld and the row says what it is.
    const swept = !!session?.closed_by_sweep
    const killsKnown = !swept && session?.kills_count > 0

    // A kills panel lists victims, and every victim row can open its own. When
    // two players trade kills, A's panel shows B, B's shows A, and so on for
    // as long as anyone keeps clicking, which reads as corrupt data when it is
    // one ordinary mutual kill. A session already open further up has its
    // panel on screen; opening it again adds nothing, so it does not open.
    const shownAbove = openAbove.includes(session?.session_id)
    const expandable = killsKnown && !shownAbove

    const toggleDetails = () => {
        if (expandable) setShowDetails((prev) => !prev)
    }
    return (
        <>
            <tr
                onClick={toggleDetails}
                className={twMerge(expandable && 'cursor-pointer hover:text-blue-500')}
            >
                <td className='text-xs text-white/20'>{rowNumber + 1}</td>
                {showUsername && <td>{session.username}</td>}
                {/* Marked on every row, not only in the filtered list, so the
                    filter and the list agree about which sessions those are --
                    a toggle that reveals rows the unfiltered view refused to
                    mark is how two views of one fact start disagreeing. */}
                {session?.cheat_flagged && (
                    <td
                        className='text-red-500 text-xs'
                        title='Ended for cheating, or a cheat report was raised against this session'
                    >
                        FLAG
                    </td>
                )}
                {!session?.cheat_flagged && <td />}

                <td>
                    {expandable && (
                        <button>
                            <FontAwesomeIcon icon={showDetails ? faChevronDown : faChevronRight} />
                        </button>
                    )}
                    {killsKnown && shownAbove && (
                        <span className='text-white/30' title='This session is already expanded above'>
                            &uarr;
                        </span>
                    )}
                </td>

                <td title={`id: ${session?.server_id}`}>{session?.server_name}</td>
                <td title={session?.spawned_at}>{new Date(session?.spawned_at).toLocaleString()}</td>
                <td>{session?.ip}</td>
                <td className={twMerge(swept && 'text-white/40 italic')}>
                    {session?.duration?.split('.')?.[0]}
                </td>
                <td>{session?.fee}</td>
                <td>
                    {swept ? (
                        <span className='text-white/30' title='No result was reported for this session, so its kill count is not known.'>
                            &mdash;
                        </span>
                    ) : (
                        session?.kills_count
                    )}
                </td>
                <td className={twMerge(session?.earnings > 0 && 'text-green-500')}>{session?.earnings}</td>
                <td
                    className={twMerge(
                        session?.end_reason == 69 && 'text-red-500',
                        swept && 'text-amber-400/80'
                    )}
                    title={
                        swept
                            ? 'Closed by the orphan sweep. No result was ever reported, so the close time is an estimate.'
                            : undefined
                    }
                >
                    {swept ? 'Sweep' : endReasonFormat(session?.end_reason)}
                </td>
                {/* `stopPropagation`, or following the link also toggles the
                    kills panel on the row underneath it. */}
                <td onClick={(e) => e.stopPropagation()}>
                    <KilledBy session={session} />
                </td>
            </tr>
            {showDetails && (
                <tr>
                    <td></td>
                    <td colSpan={9} className='bg-white/5'>
                        <GameSessionKills session={session} openAbove={openAbove} />
                    </td>
                </tr>
            )}
        </>
    )
}

/** Who killed the player in this session, linked to their account.
 *
 *  Three states, kept apart on purpose. "Killed by nobody we can name" and
 *  "this session did not end in a death" are different facts, and rendering
 *  both as an empty cell is what makes a reviewer read a blank as missing data
 *  and go looking for it.
 *
 *  * a killer we can name  -> the name, linked
 *  * a death with no killer -> a dash and the reason. Fall damage and the map
 *    itself report none, and the FK is SET_NULL, so a deleted account leaves
 *    a death row behind with nobody on it.
 *  * no death row at all    -> empty, which is correct: they survived, quit or
 *    disconnected, and there is nothing to say.
 */
const KilledBy = ({ session }: { session: GameSession }) => {
    if (session?.killer_username)
        return (
            <Link
                to={`/team-tools/accounts/users/${session.killer_username}`}
                className='text-blue-400 hover:underline'
                title='Open this account'
            >
                {session.killer_username}
            </Link>
        )

    // An id with no name behind it. Shown rather than hidden, because "there
    // was a killer" is the useful half and the account page is addressed by
    // username, so there is no link to build.
    if (session?.killer_id) return <span title='This account has no username'>#{session.killer_id}</span>

    if (session?.died)
        return (
            <span className='text-white/30' title='A death was reported for this session with no killer on it: the map, fall damage, or an account since deleted.'>
                &mdash;
            </span>
        )

    return null
}

function endReasonFormat(endReason: number) {
    return {
        1: 'Death',
        2: 'Survival',
        3: 'Disconnect',
        4: 'Quit',
        // The server removed them for a bad connection. Paid as a disconnect;
        // it had no label and rendered blank (audit RAW-12).
        5: 'Removed (latency)',
        69: 'Cheat',
    }[endReason]
}
